import { QueryFactory } from 'services/api';
import { triggerDownloadFileDialog } from 'helpers/file-helper';
import {
  CustomCommandExecutedEventArgs,
  DXR,
  RichEdit,
} from 'components/devexpress/RichEdit/dx-richedit';
import { RefObject, useEffect, useRef } from 'react';
import {
  DocumentExportFormat,
  FileResponse,
} from 'services/api/api-client.types.ts';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import { switchDocumentExportLoader } from './DocumentExportLoader/unsafeDocumentExportLoader';
import { assertNever } from 'helpers/assert-never';
import { CustomDropDownInnerProps } from './richEdit/types';
import { toast } from 'react-toastify';
import { Toast } from 'components/uikit/toast/Toast';
import {
  getToastClassNameFunc,
  ToastStyle,
} from 'components/uikit/toast/ToastContainer';
import { FeatureFlags } from 'application/constants/env-variables';
import { ExpiredVariablesToast } from './expiredVariablesToast/ExpiredVariablesToast';
import { showErrorToast } from 'components/uikit/toast/showToast';

const exportDocxId = 'docx-export';
const exportRtfId = 'rtf-export';
const exportTxtId = 'txt-export';
const exportPdfId = 'pdf-export';

type DocumentExportDropdownProps = {
  type: 'document';
  id?: number | null;
  saveDocument: () => Promise<boolean>;
  projectId: number;
  revisionId: number;
  isVariableCodesModeRef: RefObject<boolean>;
  updateVariables: (
    variableIds: number[],
    onAfterOpen?: () => any,
  ) => Promise<void>;
  refreshVariables: () => Promise<void>;
};

export function useDocumentExportDropdown(
  props: Omit<DocumentExportDropdownProps, 'type'>,
): CustomDropDownInnerProps {
  const documentIdRef = useRef<number | null | undefined>(props.id);
  useEffect(() => {
    documentIdRef.current = props.id;
  }, [props.id]);

  return useExportDropdown(
    async (documentContent, format) => {
      const docId = documentIdRef.current ?? null;
      return await QueryFactory.DocumentExportClient.deferExportDocument(
        {
          documentContent,
          revisionId: props.revisionId,
          documentId: docId,
        },
        format,
      );
    },
    {
      type: 'document',
      ...props,
    },
  );
}

type TemplateExportDropdownProps = {
  type: 'template';
  id: number;
};

export function useTemplateExportDropdown(
  props: Omit<TemplateExportDropdownProps, 'type'>,
): CustomDropDownInnerProps {
  return useExportDropdown(
    async (documentContent, format) => {
      return await QueryFactory.DocumentExportClient.deferExportTemplate(
        {
          documentContent,
        },
        format,
      );
    },
    { type: 'template', ...props },
  );
}

function useExportDropdown(
  onExport: (
    documentContent: string,
    format: DocumentExportFormat,
  ) => Promise<FileResponse>,
  props: DocumentExportDropdownProps | TemplateExportDropdownProps,
): CustomDropDownInnerProps {
  const i18n = useScopedTranslation('Page.DocumentEditor.Export');

  const isDocument = props.type === 'document';

  const action = async (
    richEdit: RichEdit,
    e: CustomCommandExecutedEventArgs,
  ) => {
    if (
      ![exportPdfId, exportDocxId, exportRtfId, exportTxtId].includes(
        e.commandName,
      )
    ) {
      return;
    }

    if (isDocument && props.isVariableCodesModeRef)
      richEdit.document.fields.showAllFieldResults(true);

    try {
      switchDocumentExportLoader(richEdit, true);
      let format: DocumentExportFormat | null = null;

      switch (e.commandName) {
        case exportPdfId:
          format = DocumentExportFormat.Pdf;
          break;
        case exportDocxId:
          richEdit.adjust();
          format = DocumentExportFormat.OpenXml;
          break;
        case exportRtfId:
          format = DocumentExportFormat.Rtf;
          break;
        case exportTxtId:
          richEdit.downloadDocument(DXR.DocumentFormat.PlainText);
          return;
      }
      if (!format) throw new Error('Unsupported export format');

      const triggerPrintFromServer = () =>
        printFromServer(richEdit, format, onExport, props.type, props.id);

      if (
        FeatureFlags.documentExportingWithVariableUpdateEnabled() &&
        isDocument &&
        props.id
      ) {
        if (richEdit.hasUnsavedChanges) await props.saveDocument();

        const getExpiredVariables = async () => {
          return await QueryFactory.DocumentClient.getExpiredVariables(
            props.id!,
          );
        };

        const [exportResult, expiredVariablesResult] = await Promise.allSettled(
          [triggerPrintFromServer(), getExpiredVariables()],
        );
        if (exportResult.status === 'rejected') throw exportResult.reason;

        const triggerDownload = exportResult.value;
        triggerDownload();

        if (
          expiredVariablesResult.status === 'fulfilled' &&
          expiredVariablesResult.value?.length > 0
        ) {
          const expiredVariables = expiredVariablesResult.value;
          props.refreshVariables();

          toast.warning(
            ({ closeToast }) => (
              <Toast
                title={i18n.t('ExpiredVariableToast.title')}
                description={
                  <ExpiredVariablesToast
                    expiredVariables={expiredVariables}
                    triggerExport={async () => {
                      closeToast();

                      await props.updateVariables(
                        expiredVariables.map((v) => v.variableId),
                        async () => {
                          switchDocumentExportLoader(richEdit, true);
                          const triggerDownload =
                            await triggerPrintFromServer();
                          triggerDownload();
                          switchDocumentExportLoader(richEdit, false);
                        },
                      );
                    }}
                  />
                }
              />
            ),
            {
              className: getToastClassNameFunc(ToastStyle.Wide),
              autoClose: false,
            },
          );
        } else if (expiredVariablesResult.status === 'rejected')
          throw expiredVariablesResult.reason;
      } else {
        const triggerDownload = await triggerPrintFromServer();
        triggerDownload();
      }
    } catch (error) {
      if (error instanceof Blob && error.type === 'application/problem+json') {
        const errorText = await error.text();
        error = JSON.parse(errorText);
      }

      showErrorToast(error);
    } finally {
      switchDocumentExportLoader(richEdit, false);
    }
  };

  return {
    id: 'export',
    enabled: true,
    text: 'Export',
    items: [
      { id: exportPdfId, text: i18n.t('DropDown.PdfButton') },
      { id: exportDocxId, text: i18n.t('DropDown.DocxButton') },
      { id: exportRtfId, text: i18n.t('DropDown.RtfButton') },
      { id: exportTxtId, text: i18n.t('DropDown.TxtButton') },
    ],
    icon: 'export',
    showText: true,
    action,
  };
}

function printFromServer(
  richEdit: RichEdit,
  format: DocumentExportFormat,
  onExport: (
    documentContent: string,
    format: DocumentExportFormat,
  ) => Promise<FileResponse>,
  defaultDocumentName: string,
  documentId?: number | null,
): Promise<() => void> {
  return new Promise((resolve, reject) => {
    richEdit.exportToBase64(async (docBase64) => {
      try {
        if (documentId) {
          richEdit.documentName =
            await QueryFactory.DocumentExportClient.getExportName(documentId);
        }

        const exportedDocFile = await onExport(docBase64, format);

        const newBlob = new Blob([exportedDocFile.data], {
          type: exportedDocFile.data.type,
        });

        const triggerDownload = () =>
          triggerDownloadFileDialog(
            newBlob,
            (richEdit.documentName ?? defaultDocumentName) +
              getFileExtension(format),
          );

        resolve(triggerDownload);
      } catch (e) {
        reject(e);
      }
    }, DXR.DocumentFormat.OpenXml);
  });
}

export const getFileExtension = (format: DocumentExportFormat) => {
  switch (format) {
    case DocumentExportFormat.OpenXml:
      return '.docx';
    case DocumentExportFormat.Rtf:
      return '.rtf';
    case DocumentExportFormat.Pdf:
      return '.pdf';
    case DocumentExportFormat.Dotx:
      return '.dotx';
    default:
      assertNever(format);
  }
};
